import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { Observable, Subscription, shareReplay } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { StatusCardWorkspaceService } from '../../shared-services/status-card-workspace.service';

interface DetailTab {
  key?: string;
  label?: string;
}

interface DetailPanel {
  type?: string;
  title?: string;
  currentAssignedLabel?: string;
  assignedUsersText?: string;
  assignNewUserLabel?: string;
  assignButtonLabel?: string;
  userPlaceholder?: string;
  assignedUsers?: string[];
  userOptions?: string[];
  apiUrl?: string;
  method?: string;
  skipDefaultPayload?: boolean;
  customPayload?: Record<string, unknown>;
  authDetailsKey?: string;
  dataKey?: string;
  mappingsTitle?: string;
  mappingsColumns?: Array<{ key?: string; label?: string; editable?: boolean }>;
  updateMappingsLabel?: string;
  mappings?: Array<Record<string, string>>;
  sections?: Array<{
    title?: string;
    cards?: Array<{ title?: string; description?: string; active?: boolean }>;
    table?: {
      apiUrl?: string;
      method?: string;
      skipDefaultPayload?: boolean;
      customPayload?: Record<string, unknown>;
      entityKey?: string;
      permissionRequest?: {
        apiUrl?: string;
        method?: string;
        skipDefaultPayload?: boolean;
        customPayload?: Record<string, unknown>;
      };
      columns?: Array<{ key?: string; label?: string; type?: string }>;
      rows?: Array<Record<string, unknown>>;
    };
  }>;
}

export interface UserRoleChange {
  user_name: string;
  user_email: string;
  roles: string[];
}

export interface EntityPermissionChange {
  userRole: string;
  entityPermissions: Record<string, {
    read: string[];
    write: string[];
    approval: string;
  }>;
}

@Component({
  selector: 'app-tabbed-detail-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tabbed-detail-panel.component.html',
  styleUrl: './tabbed-detail-panel.component.scss',
})
export class TabbedDetailPanelComponent implements OnChanges, OnDestroy {
  private static readonly authDetailsRequests = new Map<string, Observable<any>>();
  private static readonly entityRequests = new Map<string, Observable<any>>();
  private static readonly roleEntityPermissionRequests = new Map<string, Observable<any>>();
  private readonly configService = inject(ConfigService);
  private readonly workspaceService = inject(StatusCardWorkspaceService);
  private workspaceSubscription?: Subscription;
  private workspaceActionSubscription?: Subscription;
  private workspaceSource = '';
  globalHierarchyPanel: any = null;
  globalHierarchyActive = false;
  private _data: any;
  @Input() set data(value: any) {
    this._data = value;
    this.bindWorkspace(value);
  }
  get data(): any {
    return this._data;
  }
  @Input() contextTitle = '';
  @Input() userRoleChanges: UserRoleChange[] = [];
  @Input() entityPermissionChanges: EntityPermissionChange[] = [];
  @Output() userRoleChange = new EventEmitter<UserRoleChange>();
  @Output() entityPermissionChange = new EventEmitter<EntityPermissionChange>();

  activeTabKey = '';
  private selectedPermissionCards = new Set<string>();
  private selectedPermissionChecks = new Map<string, boolean>();
  private removedAssignedUsers = new Map<string, Set<string>>();
  private authDetailsSubscription?: Subscription;
  private entityPermissionsSubscription = new Subscription();
  private loadedEntityUrls = new Set<string>();
  private entityRowsByUrl = new Map<string, Array<Record<string, unknown>>>();
  private permissionSectionsValue: NonNullable<DetailPanel['sections']> = [];
  private loadedAuthDetails: Array<{ emailId?: string; userId?: string; userRoles?: string }> = [];
  private assignedUserListValue: string[] = [];
  private assignedUserDetailsValue: Array<{ userId: string; email: string }> = [];
  private userOptionsValue: string[] = [];
  private mappingsValue: Array<Record<string, string>> = [];
  selectedUserValues: string[] = [];
  toastVisible = false;
  toastMessage = '';
  private toastTimer?: ReturnType<typeof setTimeout>;
  private pendingEmailMappings = new Map<string, string>();

  ngOnChanges(changes: SimpleChanges): void {
    const panelContextChanged = !!changes['data'] || !!changes['contextTitle'];
    if (!panelContextChanged) {
      if (changes['userRoleChanges']) {
        this.updateDerivedAuthDetails();
      }
      return;
    }

    if (!this.tabs.length) {
      this.activeTabKey = '';
      return;
    }

    const configured = String(this.panelConfig?.activeTab ?? '').trim();
    const currentStillExists = this.tabs.some((tab) => tab.key === this.activeTabKey);
    const configuredExists = this.tabs.some((tab) => tab.key === configured);

    if (!currentStillExists) {
      this.activeTabKey = configuredExists ? configured : String(this.tabs[0].key);
    }

    this.syncSelectedPermissionCards();
    this.syncSelectedPermissionChecks();
    this.updateDerivedAuthDetails();
    this.loadAuthDetails();
    this.loadPermissionEntities();
  }

  ngOnDestroy(): void {
    this.workspaceSubscription?.unsubscribe();
    this.workspaceActionSubscription?.unsubscribe();
    this.authDetailsSubscription?.unsubscribe();
    this.entityPermissionsSubscription.unsubscribe();
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  private bindWorkspace(value: any): void {
    const source = String(value?.binding?.source ?? '').trim();
    if (!source || source === this.workspaceSource) return;
    this.workspaceSubscription?.unsubscribe();
    this.workspaceSource = source;
    this.workspaceActionSubscription?.unsubscribe();
    this.workspaceActionSubscription = this.workspaceService.actions$(source).subscribe((action) => {
      if (action.type !== 'toggleHierarchy') return;
      this.globalHierarchyPanel = action.payload?.panel ?? this.globalHierarchyPanel;
      this.globalHierarchyActive = !this.globalHierarchyActive;
    });
    this.workspaceSubscription = this.workspaceService.state$(source).subscribe((state) => {
      if (!state) return;
      if (state.detailPanel) this._data = state.detailPanel;
      this.contextTitle = String(state.item?.title ?? '');
      this.refreshPanelContext();
    });
  }

  private refreshPanelContext(): void {
    if (!this.tabs.length) {
      this.activeTabKey = '';
      return;
    }
    const configured = String(this.panelConfig?.activeTab ?? '').trim();
    const currentStillExists = this.tabs.some((tab) => tab.key === this.activeTabKey);
    const configuredExists = this.tabs.some((tab) => tab.key === configured);
    if (!currentStillExists) {
      this.activeTabKey = configuredExists ? configured : String(this.tabs[0].key);
    }
    this.syncSelectedPermissionCards();
    this.syncSelectedPermissionChecks();
    this.updateDerivedAuthDetails();
    this.loadAuthDetails();
    this.loadPermissionEntities();
  }

  get tabs(): DetailTab[] {
    const configuredTabs = Array.isArray(this.panelConfig?.tabs) ? this.panelConfig.tabs : [];
    if (configuredTabs.length) {
      return configuredTabs
        .filter((tab: DetailTab) => String(tab?.key ?? '').trim() && String(tab?.label ?? '').trim());
    }

    if (!this.panelConfig?.panels || typeof this.panelConfig.panels !== 'object') {
      return [];
    }

    return Object.keys(this.panelConfig.panels)
      .map((key) => ({
        key,
        label: this.toLabel(key),
      }))
      .filter((tab: DetailTab) => String(tab?.key ?? '').trim() && String(tab?.label ?? '').trim());
  }

  get activePanel(): DetailPanel | null {
    const key = this.activeTabKey || this.tabs[0]?.key;
    if (!key || !this.panelConfig?.panels || typeof this.panelConfig.panels !== 'object') {
      return null;
    }

    return this.panelConfig.panels[key] ?? null;
  }

  get hasPanel(): boolean {
    return !!this.activePanel;
  }

  get assignedUsers(): string {
    const count = this.assignedUserList.length;
    return this.interpolate(
      this.activePanel?.assignedUsersText,
      { count: String(count), title: this.effectiveContextTitle },
    );
  }

  get assignedUserList(): string[] {
    const removed = this.removedAssignedUsers.get(this.assignmentStateKey);
    return removed ? this.assignedUserListValue.filter((user) => !removed.has(user)) : this.assignedUserListValue;
  }

  get assignedUserDetails(): Array<{ userId: string; email: string }> {
    return this.assignedUserDetailsValue;
  }

  get userOptions(): string[] {
    return this.userOptionsValue;
  }

  get selectedUsers(): string[] {
    return this.selectedUserValues;
  }

  get selectedUsersLabel(): string {
    if (!this.selectedUsers.length) return this.userPlaceholder;
    return this.selectedUsers.length <= 2
      ? this.selectedUsers.join(', ')
      : `${this.selectedUsers.length} users selected`;
  }

  get userPlaceholder(): string {
    return String(this.activePanel?.userPlaceholder ?? 'Select user to assign...');
  }

  get mappings(): Array<Record<string, string>> {
    return this.mappingsValue;
  }

  get updateMappingsLabel(): string {
    return String(this.activePanel?.updateMappingsLabel ?? '');
  }

  get sections(): NonNullable<DetailPanel['sections']> {
    return this.permissionSectionsValue.length
      ? this.permissionSectionsValue
      : Array.isArray(this.activePanel?.sections) ? this.activePanel.sections : [];
  }

  get currentAssignedLabel(): string {
    return this.interpolate(this.activePanel?.currentAssignedLabel, { title: this.effectiveContextTitle });
  }

  get assignNewUserLabel(): string {
    return this.interpolate(this.activePanel?.assignNewUserLabel, { title: this.effectiveContextTitle });
  }

  get assignButtonLabel(): string {
    return String(this.activePanel?.assignButtonLabel ?? '');
  }

  get mappingsTitle(): string {
    return this.interpolate(this.activePanel?.mappingsTitle, { title: this.effectiveContextTitle });
  }

  get mappingsColumns(): Array<{ key?: string; label?: string; editable?: boolean }> {
    return Array.isArray(this.activePanel?.mappingsColumns) ? this.activePanel.mappingsColumns : [];
  }

  get effectiveContextTitle(): string {
    return String(
      this.contextTitle ||
      this.panelConfig?.contextTitle ||
      this.panelConfig?.selectedTitle ||
      this.panelConfig?.roleTitle ||
      this.panelConfig?.title ||
      '',
    ).trim();
  }

  setActiveTab(key: string | undefined): void {
    const nextKey = String(key ?? '').trim();
    if (nextKey) {
      this.activeTabKey = nextKey;
      this.permissionSectionsValue = [];
      this.loadPermissionEntities();
    }
  }

  panelTitle(panel: DetailPanel | null): string {
    return String(panel?.title ?? '').replace('{{title}}', this.effectiveContextTitle);
  }

  removeAssignedUser(user: string): void {
    const record = this.getEffectiveUser(user);
    if (!record) return;
    const role = this.effectiveContextTitle;
    this.emitUserRoleChange({
      ...record,
      roles: record.roles.filter((existingRole) => this.normalizeRole(existingRole) !== this.normalizeRole(role)),
    });
    this.showToast(`${user} removed from ${role} role`);
  }

  assignSelectedUser(): void {
    const role = this.effectiveContextTitle;
    if (!this.selectedUsers.length || !role) {
      this.showToast(this.userPlaceholder);
      return;
    }
    let addedCount = 0;
    this.selectedUsers.forEach((user) => {
      const record = this.getEffectiveUser(user);
      if (!record || record.roles.some(
        (existingRole) => this.normalizeRole(existingRole) === this.normalizeRole(role),
      )) return;
      this.emitUserRoleChange({ ...record, roles: [...record.roles, role] });
      addedCount += 1;
    });
    this.selectedUserValues = [];
    this.showToast(addedCount
      ? `${addedCount} user${addedCount === 1 ? '' : 's'} added as ${role} role`
      : `Selected users are already assigned to ${role} role`);
  }

  onSelectedUserChange(value: string): void {
    this.selectedUserValues = this.selectedUserValues.includes(value)
      ? this.selectedUserValues.filter((user) => user !== value)
      : [...this.selectedUserValues, value];
  }

  onMappingEmailChange(userId: string, email: string): void {
    this.pendingEmailMappings.set(userId, email.trim());
  }

  updateEmailMappings(): void {
    if (!this.pendingEmailMappings.size) {
      this.showToast('No email mapping changes to update');
      return;
    }
    this.pendingEmailMappings.forEach((email, userId) => {
      const record = this.getEffectiveUser(userId);
      if (record) this.emitUserRoleChange({ ...record, user_email: email });
    });
    const count = this.pendingEmailMappings.size;
    this.pendingEmailMappings.clear();
    this.showToast(`${count} user email mapping${count === 1 ? '' : 's'} updated`);
  }

  permissionCardKey(sectionIndex: number, cardIndex: number): string {
    return `${sectionIndex}:${cardIndex}`;
  }

  isPermissionCardSelected(sectionIndex: number, cardIndex: number): boolean {
    return this.selectedPermissionCards.has(this.permissionCardKey(sectionIndex, cardIndex));
  }

  togglePermissionCard(sectionIndex: number, cardIndex: number): void {
    const key = this.permissionCardKey(sectionIndex, cardIndex);
    if (this.selectedPermissionCards.has(key)) {
      this.selectedPermissionCards.delete(key);
      return;
    }
    this.selectedPermissionCards.add(key);
  }

  permissionCheckKey(sectionIndex: number, rowIndex: number, columnKey: string | undefined): string {
    return `${sectionIndex}:${rowIndex}:${String(columnKey ?? '')}`;
  }

  isPermissionChecked(
    row: Record<string, unknown>,
    sectionIndex: number,
    rowIndex: number,
    columnKey: string | undefined,
  ): boolean {
    const key = this.permissionCheckKey(sectionIndex, rowIndex, columnKey);
    if (this.selectedPermissionChecks.has(key)) {
      return !!this.selectedPermissionChecks.get(key);
    }

    return !!row[String(columnKey ?? '')];
  }

  togglePermissionCheck(
    row: Record<string, unknown>,
    sectionIndex: number,
    rowIndex: number,
    columnKey: string | undefined,
  ): void {
    const key = this.permissionCheckKey(sectionIndex, rowIndex, columnKey);
    this.selectedPermissionChecks.set(
      key,
      !this.isPermissionChecked(row, sectionIndex, rowIndex, columnKey),
    );
    this.emitEntityPermissionChange(sectionIndex);
  }

  private emitEntityPermissionChange(sectionIndex: number): void {
    const table = this.sections[sectionIndex]?.table;
    if (!table) return;
    const entityKey = table.entityKey || 'entity';
    const entityPermissions: EntityPermissionChange['entityPermissions'] = {};
    (table.rows || []).forEach((row, rowIndex) => {
      const entityName = String(row[entityKey] ?? '').trim();
      if (!entityName) return;
      entityPermissions[entityName] = {
        read: this.isPermissionChecked(row, sectionIndex, rowIndex, 'read') ? ['AttributesList'] : [],
        write: this.isPermissionChecked(row, sectionIndex, rowIndex, 'write') ? ['AttributesList'] : [],
        approval: 'yes',
      };
    });
    this.dispatchEntityPermissionChange({ userRole: this.effectiveContextTitle, entityPermissions });
  }

  private emitUserRoleChange(change: UserRoleChange): void {
    const index = this.userRoleChanges.findIndex((item) => item.user_name === change.user_name);
    this.userRoleChanges = index >= 0
      ? this.userRoleChanges.map((item, itemIndex) => itemIndex === index ? change : item)
      : [...this.userRoleChanges, change];
    this.updateDerivedAuthDetails();
    this.userRoleChange.emit(change);
    if (this.workspaceSource) this.workspaceService.dispatch(this.workspaceSource, { type: 'userRoleChange', payload: change });
  }

  private dispatchEntityPermissionChange(change: EntityPermissionChange): void {
    const index = this.entityPermissionChanges.findIndex((item) => item.userRole === change.userRole);
    this.entityPermissionChanges = index >= 0
      ? this.entityPermissionChanges.map((item, itemIndex) => itemIndex === index ? change : item)
      : [...this.entityPermissionChanges, change];
    this.entityPermissionChange.emit(change);
    if (this.workspaceSource) this.workspaceService.dispatch(this.workspaceSource, { type: 'entityPermissionChange', payload: change });
  }

  onPermissionCardKeydown(event: KeyboardEvent, sectionIndex: number, cardIndex: number): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.togglePermissionCard(sectionIndex, cardIndex);
  }

  private syncSelectedPermissionCards(): void {
    this.selectedPermissionCards.clear();
    this.sections.forEach((section, sectionIndex) => {
      (section?.cards || []).forEach((card, cardIndex) => {
        if (card.active) {
          this.selectedPermissionCards.add(this.permissionCardKey(sectionIndex, cardIndex));
        }
      });
    });
  }

  private syncSelectedPermissionChecks(): void {
    this.selectedPermissionChecks.clear();
    this.sections.forEach((section, sectionIndex) => {
      const columns = section?.table?.columns || [];
      (section?.table?.rows || []).forEach((row, rowIndex) => {
        columns.forEach((column) => {
          if (column.type === 'checkbox') {
            this.selectedPermissionChecks.set(
              this.permissionCheckKey(sectionIndex, rowIndex, column.key),
              !!row[String(column.key ?? '')],
            );
          }
        });
      });
    });
  }

  private loadPermissionEntities(): void {
    if (this.activePanel?.type !== 'permissions') {
      return;
    }

    const configuredSections = Array.isArray(this.activePanel.sections) ? this.activePanel.sections : [];
    if (!this.permissionSectionsValue.length) {
      this.permissionSectionsValue = configuredSections;
    }

    configuredSections.forEach((section, sectionIndex) => {
      const table = section.table;
      if (!table?.apiUrl) {
        return;
      }

      if (this.loadedEntityUrls.has(table.apiUrl)) {
        const cachedRows = this.entityRowsByUrl.get(table.apiUrl) || [];
        this.permissionSectionsValue = configuredSections.map((configuredSection, index) =>
          index === sectionIndex
            ? { ...configuredSection, table: { ...table, rows: cachedRows } }
            : configuredSection,
        );
        this.loadRoleEntityPermissions(sectionIndex, table);
        return;
      }

      this.loadedEntityUrls.add(table.apiUrl);
      let request$ = TabbedDetailPanelComponent.entityRequests.get(table.apiUrl);
      if (!request$) {
        const apiRequest$ = this.configService.getConfig(
          table.apiUrl,
          table,
          table.customPayload || {},
          table.method || 'POST',
        );
        if (!apiRequest$) {
          return;
        }

        request$ = apiRequest$.pipe(shareReplay({ bufferSize: 1, refCount: false }));
        TabbedDetailPanelComponent.entityRequests.set(table.apiUrl, request$);
      }

      this.entityPermissionsSubscription.add(request$.subscribe({
        next: (response) => {
          const entities = Array.isArray(response)
            ? response
            : Array.isArray(response?.data) ? response.data : [];
          const entityKey = table.entityKey || 'entity';
          const rows = entities
            .map((entity: unknown) => String(entity ?? '').trim())
            .filter(Boolean)
            .map((entity: string) => ({ [entityKey]: entity, read: false, write: false }));
          this.entityRowsByUrl.set(table.apiUrl!, rows);

          this.permissionSectionsValue = configuredSections.map((configuredSection, index) =>
            index === sectionIndex
              ? { ...configuredSection, table: { ...table, rows } }
              : configuredSection,
          );
          this.syncSelectedPermissionChecks();
          this.loadRoleEntityPermissions(sectionIndex, table);
        },
        error: (error) => {
//        const   response=[
//     "entity_store",
//     "role_permissions",
//     "auth_api_config",
//     "user_details",
//     "audit_trace",
//     "employee"
// ]
//           const entities=response;
//           const entityKey = table.entityKey || 'entity';
//           const rows = entities
//             .map((entity: unknown) => String(entity ?? '').trim())
//             .filter(Boolean)
//             .map((entity: string) => ({ [entityKey]: entity, read: false, write: false }));
//           this.entityRowsByUrl.set(table.apiUrl!, rows);

//           this.permissionSectionsValue = configuredSections.map((configuredSection, index) =>
//             index === sectionIndex
//               ? { ...configuredSection, table: { ...table, rows } }
//               : configuredSection,
//           );
//           this.syncSelectedPermissionChecks();
//           this.loadRoleEntityPermissions(sectionIndex, table);
          console.error('TabbedDetailPanel: failed to load permission entities', error);
        },
      }));
    });
  }

  private loadRoleEntityPermissions(
    sectionIndex: number,
    table: NonNullable<NonNullable<DetailPanel['sections']>[number]['table']>,
  ): void {
    const userRole = this.effectiveContextTitle;
    const permissionRequest = table.permissionRequest;
    if (!permissionRequest?.apiUrl || !userRole) {
      return;
    }

    const cacheKey = `${permissionRequest.apiUrl}:${userRole}`;
    let request$ = TabbedDetailPanelComponent.roleEntityPermissionRequests.get(cacheKey);
    if (!request$) {
      const requestConfig = {
        ...permissionRequest,
        customPayload: {
          ...(permissionRequest.customPayload || {}),
          userRole,
        },
      };
      const apiRequest$ = this.configService.getConfig(
        permissionRequest.apiUrl,
        requestConfig,
        {},
        permissionRequest.method || 'POST',
      );
      if (!apiRequest$) {
        return;
      }

      request$ = apiRequest$.pipe(shareReplay({ bufferSize: 1, refCount: false }));
      TabbedDetailPanelComponent.roleEntityPermissionRequests.set(cacheKey, request$);
    }

    this.entityPermissionsSubscription.add(request$.subscribe({
      next: (response) => {
        this.applyRoleEntityPermissions(sectionIndex, table, response);
      },
      error: (error) => {
        // const temporaryResponse = {
        //   entityPermissions: {
        //     entity_store: {
        //       read: ['AttributesList'],
        //       write: ['AttributesList'],
        //       approval: 'yes',
        //     },
        //     role_permissions: {
        //       read: ['AttributesList'],
        //       write: [],
        //       approval: 'no',
        //     },
        //     auth_api_config: {
        //       read: [],
        //       write: ['AttributesList'],
        //       approval: 'yes',
        //     },
        //     employee: {
        //       read: ['AttributesList'],
        //       write: ['AttributesList'],
        //       approval: 'yes',
        //     },
        //   },
        // };
        // this.applyRoleEntityPermissions(sectionIndex, table, temporaryResponse);
        console.error('TabbedDetailPanel: failed to load role entity permissions', error);
      },
    }));
  }

  private applyRoleEntityPermissions(
    sectionIndex: number,
    table: NonNullable<NonNullable<DetailPanel['sections']>[number]['table']>,
    response: any,
  ): void {
    const pendingChange = this.entityPermissionChanges.find(
      (change) => this.normalizeRole(change.userRole) === this.normalizeRole(this.effectiveContextTitle),
    );
    const permissions = pendingChange?.entityPermissions
      ?? response?.entityPermissions
      ?? response?.data?.entityPermissions
      ?? {};
    const entityKey = table.entityKey || 'entity';
    this.permissionSectionsValue = this.permissionSectionsValue.map((section, index) => {
      if (index !== sectionIndex || !section.table) {
        return section;
      }

      const rows = (section.table.rows || []).map((row) => {
        const entityName = String(row[entityKey] ?? '');
        const entityPermission = permissions?.[entityName];
        return {
          ...row,
          read: Array.isArray(entityPermission?.read) && entityPermission.read.length > 0,
          write: Array.isArray(entityPermission?.write) && entityPermission.write.length > 0,
        };
      });
      return { ...section, table: { ...section.table, rows } };
    });
    this.syncSelectedPermissionChecks();
  }

  private loadAuthDetails(): void {
    const panel = this.activePanel;
    if (panel?.type !== 'user-assignments' || !panel.apiUrl || this.loadedAuthDetails.length) {
      return;
    }

    let request$ = TabbedDetailPanelComponent.authDetailsRequests.get(panel.apiUrl);
    if (!request$) {
      const apiRequest$ = this.configService.getConfig(
        panel.apiUrl,
        panel,
        {},
        panel.method || 'POST',
      );
      if (!apiRequest$) {
        return;
      }

      request$ = apiRequest$.pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      TabbedDetailPanelComponent.authDetailsRequests.set(panel.apiUrl, request$);
    }

    this.authDetailsSubscription?.unsubscribe();
    this.authDetailsSubscription = request$.subscribe({
      next: (response) => {
        this.loadedAuthDetails = this.extractAuthDetails(response, panel);
        this.updateDerivedAuthDetails();
      },
      error: (error) => {
        console.error('TabbedDetailPanel: failed to load auth details', error);
//         const response={
//     "authDetails": [
//         {
//             "emailId": "eugentiastutz@cummins.com",
//             "userId": "eugeniastutz",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "hari.krishnan@simadvisorypartner.com",
//             "userId": "hari",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "haripriya.s@simadvisorytech.com",
//             "userId": "haripriya.s",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "jessica.mcbrayer@cummins.com",
//             "userId": "jessica.mcbrayer",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "jill.moore@cummins.com",
//             "userId": "jill.moore",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "john.j.canellas@cummins.com",
//             "userId": "john.j.canellas",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "john.krainik@cummins.com",
//             "userId": "john.krainik",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "john.l.moreau@cummins.com",
//             "userId": "john.l.moreau",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "joseph.stangel@cummins.com",
//             "userId": "joseph.stangel",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "jose.salinas.1@cummins.com",
//             "userId": "jose.salinas.1",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "josette.calleja@cummins.com",
//             "userId": "josette.calleja",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "julie.e.bermudez@cummins.com",
//             "userId": "julie.e.bermudez",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "justin.phillips@cummins.com",
//             "userId": "justin.phillips",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "kara.smith@cummins.com",
//             "userId": "kara.smith",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "karyn.nummy@cummins.com",
//             "userId": "karyn.nummy",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "kasthuri.siddartha@simadvisorytech.com",
//             "userId": "kasthuri.siddartha",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "keisha.muzaire.muhangi@cummins.com",
//             "userId": "keisha.muzaire.muhangi",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "keith.b.higdon@cummins.com",
//             "userId": "keith.b.higdon",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "keith.w.rosario@cummins.com",
//             "userId": "keith.w.rosario",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "kevin.j.darrow@cummins.com",
//             "userId": "kevin.j.darrow",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "kimberly.stekovich@cummins.com",
//             "userId": "kimberly.stekovich",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "kiran.kc@simadvisorytech.com",
//             "userId": "kirankc",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "kishan.singh@simadvisory.com",
//             "userId": "kishan",
//             "userRoles": "{Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "krishna.chetty@simadvisory.com",
//             "userId": "krishna",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "krithika.k@simadvisory.com",
//             "userId": "krithika",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "kudret.becerikli@cummins.com",
//             "userId": "kudret.becerikli",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "kyle.kenyon@cummins.com",
//             "userId": "kyle.kenyon",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "kyle.mcdonald@cummins.com",
//             "userId": "kyle.mcdonald",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "lakhan.singh@simadvisorypartner.com",
//             "userId": "lakhan",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "lakhan.singh@simadvisorypartner1.com",
//             "userId": "lakhantest",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "laura.husband@cummins.com",
//             "userId": "laura.husband",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "layana.abraham@simadvisorypartner.com",
//             "userId": "layana",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "leeandria.wills@cummins.com",
//             "userId": "leeandria.wills",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "leeanne.izzard@cummins.com",
//             "userId": "leeanne.izzard",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "luke.j.mosier@cummins.com",
//             "userId": "luke",
//             "userRoles": "{default-roles-spriced_nrp, repower_view, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "shruthi.r@simadvisory1.com",
//             "userId": "luke.hayward",
//             "userRoles": "{repower_view, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "madhav@simadvisorypartner.com",
//             "userId": "madhav",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "mahidhar.reddy@simadvisorytech.com",
//             "userId": "mahidhar",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "manoj.hm@simadvisorytech.com",
//             "userId": "manoj.hm",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "mariana.pivetta@cummins.com",
//             "userId": "mariana.pivetta",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "marius.wolz@cummins.com",
//             "userId": "marius.wolz",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "mark.gogel@cummins.com",
//             "userId": "mark.gogel",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "mark.hansen@cummins.com",
//             "userId": "mark.hansen",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "mathieu.bellavance@cummins.com",
//             "userId": "mathieu.bellavance",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "melik.meloian@cummins.com",
//             "userId": "melik.meloian",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "michael.stanley@cummins.com",
//             "userId": "michael.stanley",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "michelle.curry@cummins.com",
//             "userId": "michelle.curry",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "mike.lee@cummins.com",
//             "userId": "mike.lee",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "misty.scheffel@cummins.com",
//             "userId": "misty.scheffel",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "rahul.balakrishnan@cummins.com",
//             "userId": "ml186@cummins.com",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "moloy.kc@simadvisorypartner.com",
//             "userId": "moloy",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "monika.dhongade@simadvisorytech.com",
//             "userId": "monika",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "mornay.annandale@cummins.com",
//             "userId": "mornay.annandale",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "mounika.c@simadviorytech.com",
//             "userId": "mounika",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "anu.johny@simadvisorypartner.com",
//             "userId": "anu",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "naya.om@simadvisory.com",
//             "userId": "nayana",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "michael.r.aufdermauer@cummins.com",
//             "userId": "michael.r.aufdermauer",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "muhammed.yazeen@simadvisorypartner.com",
//             "userId": "muhammed",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "mukil.manohar@simadvisorypartner.com",
//             "userId": "mukil",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "nandraj.patil@simadvisorypartner.com",
//             "userId": "nandraj",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "nathan.summers@cummins.com",
//             "userId": "nathan.summers",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "naveenram.lakshminarayanan@simadvisorypartner.com",
//             "userId": "naveenram",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "m.navyasree@simadvisorytech.com",
//             "userId": "navyasree",
//             "userRoles": "{default-roles-spriced_nrp, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "neelima.g@simadvisorytech.com",
//             "userId": "neelima",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "neeraj.bhargava@simadvisory.com",
//             "userId": "neeraj",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "nicholas.kryder@cummins.com",
//             "userId": "nicholas.kryder",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "nilanshi.patel@simadvisory.com",
//             "userId": "nilanshi",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "nimmi.kiran@simadvisorypartner.com",
//             "userId": "nimmi",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "nitin.chauhan@simadvisory.com",
//             "userId": "nitin",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "neeraj@siml083",
//             "userId": "nrjdev",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "ji.yang@cummins.com",
//             "userId": "ovzpt9rmkr16aklptqglvihwcopozzdl0pf2xv5opjg",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "patrick.zimmermann@cummins.com",
//             "userId": "patrick.zimmermann",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "pavit.hora@simadvisorypartner.com",
//             "userId": "pavit",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "pawel.stolecki@cummins.com",
//             "userId": "pawel.stolecki",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "praveen.kumar@cummins.com",
//             "userId": "pb7szvwqjacq9knp8r12shh20u_3txdztq0e68qqyeu",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "pooja.n@simadvisorypartner.com",
//             "userId": "pooja",
//             "userRoles": "{g_NRP_CPM_Lead, default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "prabhat.upadhyay@simadvisory.com",
//             "userId": "prabath",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "pradeep.hosamani@simadvisorypartner.com",
//             "userId": "pradeep",
//             "userRoles": "{Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "prajwal.kk@simadvisorytech.com",
//             "userId": "prajwal",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "prasanna.sr@simadvisorytech.com",
//             "userId": "prasanna",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "prasanth.reddy@simadvisorytech.com",
//             "userId": "prasanth_reddy",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "praveen.kumar@simadvisory.com",
//             "userId": "praveen",
//             "userRoles": "{Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "prithviraj.reddy@simadvisorytech.com",
//             "userId": "prithviraj",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "priyanka.naregal@simadvisorytech.com",
//             "userId": "priyanka",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "puneet.chouksey@simadvisory.com",
//             "userId": "puneet",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "puneet.chouksey@cummins.com",
//             "userId": "puneet.chouksey",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "vivek.bhagat@cummins.com",
//             "userId": "qp514@cummins.com",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "rachel.myslak@cummins.com",
//             "userId": "rachel.myslak",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "radha.gm@simadvisorytech.com",
//             "userId": "radha",
//             "userRoles": "{Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "rahul.shidid@simadvisorypartner.com",
//             "userId": "rahul",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "rajan.bhagat@simadvisory.com",
//             "userId": "rajan",
//             "userRoles": "{g_CPM_Associate_Brazil, default-roles-spriced_nrp, Admin, External, g_NRP_Admin, g_CPM_Associate, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "j.ramesh@simadvisorypartner.com",
//             "userId": "ramesh",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "raviraj.meena@simadvisorytech.com",
//             "userId": "raviraj",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "ravitheja.reddy@simadvisorytech.com",
//             "userId": "ravitheja",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "reena.py@simadvisorytech.com",
//             "userId": "reena",
//             "userRoles": "{default-roles-spriced_nrp, External, Admin, g_NRP_Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "repoweradmin@simadvisorytech.com",
//             "userId": "repower_admin",
//             "userRoles": "{Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "arijit.jana@simadvisorytech.com",
//             "userId": "arijit.jana",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "arjun.meleppatt@simadvisorypartner.com",
//             "userId": "arjun",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "arpan.jumpstart@simadvisorypartner.com",
//             "userId": "arpan",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "asif.azmi@simadvisorypartner.com",
//             "userId": "asif",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "babu.ekambaram@simadvisorytech.com",
//             "userId": "babu",
//             "userRoles": "{default-roles-spriced_nrp, g_NRP_chn_PGM, g_NRP_Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "bahadir.sengul@cummins.com",
//             "userId": "bahadir.sengul",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "barry.r.warn@cummins.com",
//             "userId": "barry.r.warn",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "benjamin.schilling@cummins.com",
//             "userId": "benjamin.schilling",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "bharath.vn@simadvisorypartner.com",
//             "userId": "bharath",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "hema@simadvisorytech.com",
//             "userId": "hema",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "mounika.keerti@simadvisory.com",
//             "userId": "mounika.k",
//             "userRoles": "{default-roles-spriced_nrp, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "brandon.d.weaver@cummins.com",
//             "userId": "brandon.d.weaver",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "brandon.isbell@cummins.com",
//             "userId": "brandon.isbell",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "brent.lilly@cummins.com",
//             "userId": "brent.lilly",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "brett.keaton@cummins.com",
//             "userId": "brett.keaton",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "brian.breaux@cummins.com",
//             "userId": "brian.breaux",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "brian.pinkstaff@cummins.com",
//             "userId": "brian.pinkstaff",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "britt.osborn@cummins.com",
//             "userId": "britt.osborn",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "brodie.trahan@cummins.com",
//             "userId": "brodie.trahan",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "brooke.ames@cummins.com",
//             "userId": "brooke.ames",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "chahida.el.baghdadi@cummins.com",
//             "userId": "chahida.el.baghdadi",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "chaitanya.gupta@simadvisorytech.com",
//             "userId": "chaitanya",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "charles.a.smith@cummins.com",
//             "userId": "charles.a.smith",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "chris.m.mcgough@cummins.com",
//             "userId": "chris.m.mcgough",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "christof.stasch@cummins.com",
//             "userId": "christof.stasch",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "christopher.t.nelson@cummins.com",
//             "userId": "christopher.t.nelson",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "clay@simadvisory.com",
//             "userId": "clay_g",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "daniel.coppola@cummins.com",
//             "userId": "daniel.coppola",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "david.sturdy@cummins.com",
//             "userId": "david.sturdy",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "david.towse@cummins.com",
//             "userId": "david.towse",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "dean.perham@cummins.com",
//             "userId": "dean.perham",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "deepak.jumpstart@simadvisorypartner.com",
//             "userId": "deepak",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "deepak222@simadvisorypartner.com",
//             "userId": "deepak2",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "deepak.kumar@simadvisorytech.com",
//             "userId": "deepak.kumar",
//             "userRoles": "{Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "dhanush.tallam@simadvisorytech.com",
//             "userId": "dhanush",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "ie998@cummins.com",
//             "userId": "dinesh.r.gudibandi",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "dipika.paul@simadvisorypartner.com",
//             "userId": "dipika",
//             "userRoles": "{Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "dipika9.paul@simadvisorypartner.com",
//             "userId": "dipikatest",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "divya.datta@simadvisory.com",
//             "userId": "divya",
//             "userRoles": "{Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "douglas.keele@cummins.com",
//             "userId": "douglas.keele",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "edoardo.giussani@cummins.com",
//             "userId": "edoardo.giussani",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "wang.fang@cummins.com",
//             "userId": "e-id9sqogd2pzhwcu2naeiiiir-52zswgl1mjwprvha",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "elaine.hilber@cummins.com",
//             "userId": "elaine.hilber",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "enrique.serizawa@cummins.com",
//             "userId": "enrique.serizawa",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "eric.k.mccoy@cummins.com",
//             "userId": "eric.k.mccoy",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "eugenia.stutz@cummins.com",
//             "userId": "eugenia.stutz",
//             "userRoles": "{repower_view, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "fabio.defranceschi@cummins.com",
//             "userId": "fabio.defranceschi",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "ganesh.garikapati@simadvisorytech.com",
//             "userId": "ganesh",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "garry.talbot@cummins.com",
//             "userId": "garry.talbot",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "gayathri.ashok@simadvisory.com",
//             "userId": "gayathri",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "xq626@cummins.com",
//             "userId": "gayathri.ashok",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "geeta.alugolu@simadvisory.com",
//             "userId": "geeta",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "glen.gaines@cummins.com",
//             "userId": "glen.gaines",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "gopika.murali@simadvisorypartner.com",
//             "userId": "gopika",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "gopinath.manavalan@simadvisorypartner.com",
//             "userId": "gopinath",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "grace.mcgee@cummins.com",
//             "userId": "grace.mcgee",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "graham.cook@cummins.com",
//             "userId": "graham.cook",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "hareena.khatwani@simadvisorytech.com",
//             "userId": "hareena",
//             "userRoles": "{Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "mark.almon@cummins.com",
//             "userId": "mark.almon",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "tom.lamano@cummins.com",
//             "userId": "tom.lamano",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "tori.l.hamill@cummins.com",
//             "userId": "tori.l.hamill",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "troy.lovell@cummins.com",
//             "userId": "troy.lovell",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "vinay.kumar@simadvisory.com",
//             "userId": "vinay",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "waylon.hejny@cummins.com",
//             "userId": "waylon.hejny",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "will.collins@cummins.com",
//             "userId": "will.collins",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "x.jeffery.l.van.linge@cummins.com",
//             "userId": "x.jeffery.l.van.linge",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "sunny@gmail.com",
//             "userId": "user123",
//             "userRoles": "ADMIN"
//         },
//         {
//             "emailId": "venkat@gmail.com",
//             "userId": "user456",
//             "userRoles": "{USER, ADMIN}"
//         },
//         {
//             "emailId": "akshay.sahadevan@simadvisorypartner.com",
//             "userId": "akshay",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "ali.agha@cummins.com",
//             "userId": "ali.agha",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "ali.baynes@cummins.com",
//             "userId": "ali.baynes",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "aman.kumar@simadvisory.com",
//             "userId": "aman",
//             "userRoles": "{Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "thara.r@simadvisory.com",
//             "userId": "thara.r",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "amit.kumar@simadvisorypartner.com",
//             "userId": "amit",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "anand.kumar@simadvisory.com",
//             "userId": "anand",
//             "userRoles": "{Admin, g_CPM_Associate, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "anjali.bijur@simadvisorytech.com",
//             "userId": "anjali",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "ankit.singh@simadvisory.com",
//             "userId": "ankit",
//             "userRoles": "{Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "ankitha.rajan@simadvisorypartner.com",
//             "userId": "ankitha",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "anshita.koshta@simadvisorypartner.com",
//             "userId": "anshita",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "anthony.moore@cummins.com",
//             "userId": "anthony.moore",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "aaditya.bhardwaj@simadvisory.com",
//             "userId": "aaditya",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "uo474@cummins.com",
//             "userId": "aasma.mokashi",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "abhinav.patel@simadvisorypartner.com",
//             "userId": "abhinav",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "abhinav.comrade@simadvisorytech.com",
//             "userId": "abhinav.comrade",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "abhishek.damani@cummins.com",
//             "userId": "abhishek",
//             "userRoles": "{repower_view, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "abhishek.kumar@simadvisorytech.com",
//             "userId": "abhishek.kumar",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "aditya.b@simadvisorytech.com",
//             "userId": "aditya",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "adrian.jarrin@cummins.com",
//             "userId": "adrian.jarrin",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "ajay.choudhary@simadvisorypartner.com",
//             "userId": "ajay",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "aparna.sunilkumar@simadvisorytech.com",
//             "userId": "aparna.sunilkumar",
//             "userRoles": "{default-roles-spriced_nrp, Admin, g_NRP_Admin, g_nrp_cpm, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "harsh.ratnaparkhi@simadvisory.com",
//             "userId": "harsh",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "harsh.kakadiya@simadvisory.com",
//             "userId": "harsh.k",
//             "userRoles": "{Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "harsh.lr@simadvisorytech.com",
//             "userId": "harsh.lr",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "herman.s.kersey@cummins.com",
//             "userId": "herman.s.kersey",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "himanshu.bhardwaj@simadvisory.com",
//             "userId": "himanshu",
//             "userRoles": "{Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "mohd.husain@simadvisorypartner.com",
//             "userId": "hussain",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "james.connell@cummins.com",
//             "userId": "james.connell",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "james.g.taylor@cummins.com",
//             "userId": "james.g.taylor",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "japinder.dhillon@cummins.com",
//             "userId": "japinder.dhillon",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "jason.cooper@cummins.com",
//             "userId": "jason.cooper",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "jason.e.pille@cummins.com",
//             "userId": "jason.e.pille",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "jeffery.m.dudas@cummins.com",
//             "userId": "jeffery.m.dudas",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "jerry.w.gordon@cummins.com",
//             "userId": "jerry.w.gordon",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "jessica@simadvisorytech.com",
//             "userId": "jessica",
//             "userRoles": "{g_NRP_CPM_Lead, g_NRP_Pricing, g_nrp_cpm, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "michael.somma@cummins.com",
//             "userId": "michael.somma",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "shruthi.r@simadvisory.com",
//             "userId": "shruthi",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "souvik.ghosh@simadvisorypartner.com",
//             "userId": "souvik",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "sridhar.mathaiah@simadvisorypartner.com",
//             "userId": "sridhar",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "srinath.venugopal@simadvisorytech.com",
//             "userId": "srinath",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "repoweruser@simadvisorytech.com",
//             "userId": "repower_user",
//             "userRoles": "{User, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "revathy.krishna@simadvisorypartner.com",
//             "userId": "revathy",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "richard.dorf@cummins.com",
//             "userId": "richard.dorf",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "richard.n.sutton@cummins.com",
//             "userId": "richard.n.sutton",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "richard.sparkman@cummins.com",
//             "userId": "richard.sparkman",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "ryan.mckay@cummins.com",
//             "userId": "ryan.mckay",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "sandip@simadvisory.com",
//             "userId": "sandip",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "sanjay.singh@cummins.com",
//             "userId": "sanjay.singh",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "satyam.kumar@simadvisorypartner.com",
//             "userId": "satyam",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "sebastian.staehle@cummins.com",
//             "userId": "sebastian.staehle",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "sertan.dogruk@cummins.com",
//             "userId": "sertan.dogruk",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "shane.d.hipsag@cummins.com",
//             "userId": "shane.d.hipsag",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "shenaaz.danka@cummins.com",
//             "userId": "shenaaz.danka",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "shilpa.sonawane@simadvisorypartner.com",
//             "userId": "shilpa",
//             "userRoles": "{Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "robert.conley@cummins.com",
//             "userId": "robert.conley",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "ronald.s.merkel@cummins.com",
//             "userId": "ronald.s.merkel",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "repowerpredictor@simadvisorytech.com",
//             "userId": "rpredictor_uat",
//             "userRoles": "{default-roles-spriced_nrp, repower_view, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "vinay.kumar@cummins.com",
//             "userId": "rt3wlnk4blhfjtwikpnupd1v_rg_138ydidh0j9jpuo",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "rupak.tibrewal@simadvisory.com",
//             "userId": "rupak",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "ryan.larck@cummins.com",
//             "userId": "ryan.larck",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "ryan.larsh@cummins.com",
//             "userId": "ryan.larsh",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "stephen.jefferies@cummins.com",
//             "userId": "stephen.jefferies",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "steve.nadeau@cummins.com",
//             "userId": "steve.nadeau",
//             "userRoles": "{default-roles-d_spriced}"
//         },
//         {
//             "emailId": "talari.vs@simadvisorytech.com",
//             "userId": "sunny",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "tasadak.choudhary@simadvisory.com",
//             "userId": "tasadak",
//             "userRoles": "{default-roles-spriced_nrp, Admin, default-roles-d_spriced}"
//         },
//         {
//             "emailId": "tomislav.t.budija@cummins.com",
//             "userId": "tomislav.t.budija",
//             "userRoles": "{default-roles-d_spriced}"
//         }
//     ]
// }
//         this.loadedAuthDetails = this.extractAuthDetails(response, panel!);
//         this.updateDerivedAuthDetails();
      },
    });
  }

  private extractAuthDetails(response: any, panel: DetailPanel): Array<{ emailId?: string; userId?: string; userRoles?: string }> {
    const dataKey = panel.authDetailsKey || panel.dataKey || 'authDetails';
    const source = Array.isArray(response)
      ? response
      : Array.isArray(response?.[dataKey])
        ? response[dataKey]
        : Array.isArray(response?.data)
          ? response.data
          : [];

    return source
      .filter((item: any) => item && typeof item === 'object')
      .map((item: any) => ({
        emailId: String(item.emailId ?? item.email ?? ''),
        userId: String(item.userId ?? item.username ?? ''),
        userRoles: String(item.userRoles ?? item.roles ?? ''),
      }));
  }

  private updateDerivedAuthDetails(): void {
    if (!this.loadedAuthDetails.length) {
      this.assignedUserListValue = [];
      this.assignedUserDetailsValue = [];
      this.userOptionsValue = [];
      this.mappingsValue = [];
      return;
    }

    const effectiveUsers = this.getEffectiveUsers();

    const assignedEffectiveUsers = effectiveUsers
      .filter((detail) => detail.roles.some(
        (role) => this.normalizeRole(role) === this.normalizeRole(this.effectiveContextTitle),
      ));

    this.assignedUserListValue = assignedEffectiveUsers
      .map((detail) => detail.user_name)
      .filter(Boolean);

    this.assignedUserDetailsValue = assignedEffectiveUsers
      .map((user) => ({ userId: user.user_name, email: user.user_email }));

    this.userOptionsValue = effectiveUsers
      .map((detail) => detail.user_name)
      .filter(Boolean);

    this.mappingsValue = assignedEffectiveUsers
      .map((detail) => ({
        userId: detail.user_name,
        email: detail.user_email,
      }));
  }

  private getEffectiveUsers(): UserRoleChange[] {
    const changes = new Map(this.userRoleChanges.map((change) => [change.user_name, change]));
    return this.loadedAuthDetails.map((detail) => {
      const userName = String(detail.userId ?? '').trim();
      return changes.get(userName) || {
        user_name: userName,
        user_email: String(detail.emailId ?? ''),
        roles: this.parseRoles(detail.userRoles),
      };
    });
  }

  private getEffectiveUser(userName: string): UserRoleChange | undefined {
    return this.getEffectiveUsers().find((user) => user.user_name === userName);
  }

  private get authDetailsForCurrentTitle(): Array<{ emailId?: string; userId?: string; userRoles?: string }> {
    const currentTitle = this.normalizeRole(this.effectiveContextTitle);
    if (!currentTitle) {
      return [];
    }

    return this.loadedAuthDetails.filter((detail) =>
      this.parseRoles(detail.userRoles).some((role) => this.normalizeRole(role) === currentTitle),
    );
  }

  private parseRoles(userRoles: string | undefined): string[] {
    return String(userRoles ?? '')
      .replace(/[{}]/g, '')
      .split(',')
      .map((role) => role.trim())
      .filter(Boolean);
  }

  private normalizeRole(role: string | undefined): string {
    return String(role ?? '').trim().toLowerCase();
  }

  private get assignmentStateKey(): string {
    return `${this.effectiveContextTitle}:${this.activeTabKey}`;
  }

  private get panelConfig(): any {
    return this.data?.detailPanel && typeof this.data.detailPanel === 'object'
      ? this.data.detailPanel
      : this.data;
  }

  private toLabel(key: string): string {
    return key
      .replace(/[-_]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));
  }

  private interpolate(template: string | undefined, values: Record<string, string>): string {
    return String(template ?? '').replace(/\{\{(\w+)\}\}/g, (_, key: string) => values[key] ?? '');
  }

  private showToast(message: string): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastMessage = message;
    this.toastVisible = true;
    this.toastTimer = setTimeout(() => {
      this.toastVisible = false;
      this.toastTimer = undefined;
    }, 3000);
  }
}
